/**
 * The app's root: the page shown, in the main layout or not, and the pages
 * kept alive.
 *
 * Pages kept alive (PreactPage's `keepAlive`: the tabs' pages) stay mounted,
 * hidden, while other pages show, so that coming back to one finds it as
 * left: where it was scrolled to, its sort and search, no loading state (and,
 * in the iOS app, the page itself under a swipe back). So that they needn't
 * handle being reused any other way, three rules hold:
 *
 * 1. A kept page comes back only to the place it was left at: the same URL
 *    (as the page itself last set it), for the same account and learning
 *    language. Anything else (another tag in the URL, a link from elsewhere)
 *    opens it afresh, in place of the one kept in its slot, as any page
 *    opens. So its options never change, and a tab's link should lead to the
 *    URL its page was left at (as /myTexts does).
 * 2. Hidden, it can't navigate: its `appRouter` ignores it. (Its URL changes
 *    while shown, `navigate` without `trigger`, are how its place is
 *    followed.) And when the account or learning language changes, the pages
 *    kept hidden are let go.
 * 3. Come back to (`shownAgain`), it fetches what may have changed
 *    meanwhile, behind what it shows: no loading state, its filters, search
 *    and scroll kept, and its requests sent through a `LatestRequest`, so
 *    that one answered late can't replace a later one's response.
 *
 * Its tab reselected while it's shown (its link followed to the URL it's
 * at), it's scrolled to the top and refreshed as when come back to, as iOS
 * tab bars do: `reselections` counts these.
 */
import * as preact from "preact";

import type { AppRouterLike } from "AppRouter";
import type PreactPage from "pages/PreactPage";
import stores from "stores/stores";
import ImpersonationBannerView from "views/ImpersonationBannerView";
import MainLayoutView from "views/MainLayoutView";
import ModalHostView from "views/ModalHostView";
import PageBackLink from "views/PageBackLink";

export type MainLayoutProps = preact.ComponentProps<typeof MainLayoutView>;

type ActivePage = {
  /** For a page kept alive, one that only it can navigate with, as shown */
  appRouter: any;
  mainLayoutProps?: MainLayoutProps;
  options?: any;
  page: PreactPage;
  /** For a page kept alive: how many times its tab's been reselected */
  reselections: number;
  renderedCallback: (renderedOptions?: any) => void;
  renderSequence: number;
  /** For a page kept alive, its slot (PreactPage's `keepAlive`) */
  slot?: string;
  /** Where it is (`currentPlace`), followed for a page kept alive */
  place: string;
  /** For a page kept alive: where it was scrolled to when last left */
  scrollY?: number;
};

type State = {
  activePage?: ActivePage;
  /** Pages kept mounted (and hidden) while others show, to return to as left */
  keptPages: ActivePage[];
  mainLayoutProps?: MainLayoutProps;
};

/** The URL, for the account and the language being learned */
const currentPlace = () =>
  `${stores.user.id.value}:${stores.user.currentLearningLanguage.value}:${window.location.pathname}${window.location.search}`;

const pageKey = (activePage: ActivePage) => {
  const { options, page, renderSequence } = activePage;
  if (!page.preserveOnRerender) {
    return `${page.name}:render:${renderSequence}`;
  }

  if (page.preserveKey) {
    return `${page.name}:preserve:${String(page.preserveKey(options))}`;
  }

  return `${page.name}:preserve`;
};

export default class AppRootView extends preact.Component<unknown, State> {
  private renderSequence = 0;
  private disposeUserWatch?: () => void;

  constructor() {
    super();
    this.state = { keptPages: [] };
  }

  componentDidMount() {
    // Pages kept hidden for another account or language are let go (the one
    // shown follows the change itself)
    this.disposeUserWatch = stores.user.onChange(
      (user) => `${user._id}:${user.currentLearningLanguage}`,
      () =>
        this.setState(({ activePage, keptPages }) => ({
          keptPages: keptPages.filter((entry) => entry === activePage),
        }))
    );
  }

  componentWillUnmount() {
    this.disposeUserWatch?.();
  }

  /** Notes where a page kept alive is scrolled to, as another is about to show */
  leavePage() {
    const leaving = this.state.activePage;
    if (leaving?.slot) {
      leaving.scrollY = window.scrollY;
    }
  }

  renderPage({
    appRouter,
    mainLayoutProps,
    options,
    page,
    renderedCallback,
  }: Omit<ActivePage, "place" | "reselections" | "renderSequence">) {
    this.renderSequence += 1;

    // A page kept alive comes back as it was left, to the place it was left
    const slot = page.keepAlive?.(options);
    const place = currentPlace();
    const kept = this.state.keptPages.find(
      (entry) =>
        entry.page.name === page.name &&
        entry.slot === slot &&
        entry.place === place
    );
    const reselected = kept !== undefined && kept === this.state.activePage;
    const activePage: ActivePage = kept
      ? {
          ...kept,
          renderedCallback,
          reselections: kept.reselections + (reselected ? 1 : 0),
        }
      : {
          appRouter: slot
            ? this.keptPageRouter(appRouter, this.renderSequence)
            : appRouter,
          mainLayoutProps,
          options,
          page,
          place,
          renderedCallback,
          reselections: 0,
          renderSequence: this.renderSequence,
          slot,
        };

    return new Promise<void>((resolve) => {
      this.setState(
        (previousState) => ({
          activePage,
          keptPages: slot
            ? [
                ...previousState.keptPages.filter(
                  (entry) => entry.slot !== slot
                ),
                activePage,
              ]
            : previousState.keptPages,
          mainLayoutProps: mainLayoutProps ?? previousState.mainLayoutProps,
        }),
        () => {
          if (kept) {
            window.scrollTo(0, reselected ? 0 : kept.scrollY ?? 0);
            renderedCallback({ alreadyScrolled: true });
          } else if (!page.deferRenderedCallback) {
            renderedCallback();
          }
          resolve();
        }
      );
    });
  }

  /** A kept page's router: ignored while it's hidden, following its place */
  private keptPageRouter(
    appRouter: AppRouterLike,
    renderSequence: number
  ): AppRouterLike {
    return {
      navigate: (path, navigateOptions) => {
        const shown = this.state.activePage;
        if (shown?.renderSequence !== renderSequence) {
          return;
        }
        appRouter.navigate(path, navigateOptions);
        if (!navigateOptions?.trigger) {
          shown.place = currentPlace();
        }
      },
    };
  }

  updateMainLayoutProps(mainLayoutProps: MainLayoutProps) {
    return new Promise<void>((resolve) => {
      this.setState({ mainLayoutProps }, () => resolve());
    });
  }

  private renderPageElement(entry: ActivePage) {
    const { appRouter, options, page, renderedCallback, reselections } = entry;
    const Child = page.Child;
    const element = (
      <Child
        key={pageKey(entry)}
        active={entry === this.state.activePage}
        appRouter={appRouter}
        options={options}
        renderedCallback={renderedCallback}
        reselections={reselections}
      />
    );
    // A page opened from another (not kept, as the tabs' are) leads back to it
    return page.showHeader && !entry.slot ? (
      <preact.Fragment key={pageKey(entry)}>
        <PageBackLink pageName={page.name} appRouter={appRouter} />
        {element}
      </preact.Fragment>
    ) : (
      element
    );
  }

  render() {
    const { activePage, keptPages, mainLayoutProps } = this.state;
    const overlays = (
      <>
        <ModalHostView sheetsOnPhones />
        <ImpersonationBannerView />
      </>
    );
    if (!activePage) {
      return overlays;
    }

    // Pages with the header, shown or kept, go in the main layout, hidden
    // while a page without it shows
    const showsLayout = activePage.page.showHeader;
    const layoutPages =
      showsLayout && !activePage.slot ? [...keptPages, activePage] : keptPages;
    const pages = layoutPages.map((entry) =>
      entry.slot ? (
        <div
          key={pageKey(entry)}
          class="keptPage"
          hidden={entry !== activePage}
        >
          {this.renderPageElement(entry)}
        </div>
      ) : (
        this.renderPageElement(entry)
      )
    );

    return (
      <>
        {!showsLayout && this.renderPageElement(activePage)}
        {layoutPages.length > 0 && (
          <div class="mainLayout" hidden={!showsLayout}>
            {mainLayoutProps ? (
              <MainLayoutView {...mainLayoutProps}>{pages}</MainLayoutView>
            ) : (
              pages
            )}
          </div>
        )}
        {overlays}
      </>
    );
  }
}
